import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import get from 'lodash-es/get';

import { HashTriggerable } from '@finder/shared/features/hash-triggerable/hash-triggerable-base';
import { HashTriggerableService } from '@finder/shared/features/hash-triggerable/hash-triggerable.service';
import { CommonModule } from '@angular/common';
import { FontIconComponent } from '../font-icon/font-icon.component';
import { ComponentHeaderComponent } from '../component-header/component-header.component';
import { SafeHtmlPipe } from '@finder/shared/utils/pipes/safe-html/safe-html.pipe';

@Component({
    selector: 'finder-collapsible-text',
    templateUrl: './collapsible-text.component.html',
    styleUrls: ['./collapsible-text.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        FontIconComponent,
        ComponentHeaderComponent,
        SafeHtmlPipe,
    ]
})
export class CollapsibleTextComponent extends HashTriggerable implements OnInit, OnDestroy, AfterViewInit {
    @Input() data: any;
    @ViewChild('content', { static: false }) content: ElementRef<HTMLDivElement>;

    icon = '';

    protected destroy$ = new Subject<void>();

    private openedIcon = '&#58134;'; // Norgie Opened
    private closedIcon = '&#58676;'; // Norgie Closed

    constructor(
        protected hashTriggerableService: HashTriggerableService,
        protected route: ActivatedRoute,
    ) {
        super(hashTriggerableService, route);
    }

    ngOnInit() {
        if (!this.data) {
            return;
        }

        this.icon = this.closedIcon;
        this.elementId = get(this.data, 'collapsibleText.parentId');
    }

    ngAfterViewInit() {
        super.ngAfterViewInit();
        this.hashTriggerableService.getActiveElementId()
            .pipe(
                filter(result => result !== void 0), // First one is undefined
                takeUntil(this.destroy$)
            )
            .subscribe(result => {
                if (result === null || result !== this.elementId) {
                    this.collapse();
                } else {
                    this.expand();
                }
            });
    }

    ngOnDestroy() {
        // Ignore the if just for coverage, there's no need to test unsubscribe
        /* istanbul ignore next */
        this.destroy$.next();
        this.destroy$.complete();
    }

    headerClick(e) {
        // Allow keyboard control for a11y
        if (e.type === 'keydown') {
            switch (e.code) {
                case 'Enter':
                case 'Space':
                    // ok...
                    e.preventDefault();
                    break;
                default:
                    return;
            }
        }

        this.toggleCss();
    }

    protected expand() {
        super.expand();
        this.icon = this.openedIcon;

        /* istanbul ignore else */
        if (this.content) {
            this.content.nativeElement.classList.add('open');
        }
    }

    protected collapse() {
        super.collapse();
        this.icon = this.closedIcon;

        /* istanbul ignore else */
        if (this.content) {
            this.content.nativeElement.classList.remove('open');
        }
    }

    private toggleCss() {
        // Toggle the icon and `open` class
        if (this.isExpanded === false) {
            this.expand();
            this.hashTriggerableService.updateHash(this.elementId);
        } else {
            this.collapse();
            this.hashTriggerableService.removeHash();
        }
    }
}
